// AI-CONTEXT: See docs/features/deep-analysis-period.md
// AI-CONTEXT: See docs/pitfalls.md#routing-url-built-here-read-by-position-there
//
// Reads and writes a card's period+compare value (CardPeriodControl's shape)
// as url query keys, so the deep-analysis page keeps the period on refresh and
// in a shared link. One module owns both directions: the builder (the
// 'Diepte-analyse' button, the period control) and the reader (the page) share
// these key names instead of each spelling them out.
//
// Keys: van / tot (primary), vvan / vtot (compare). `compare` is NOT used — it
// already belongs to the effect-pair section (?compare=<effectId>).
// vgl=1 marks a comparison in which one half is "all data" (no bounds), which
// the card's period control allows; it is only written when a half is empty,
// so an ordinary link stays van/tot/vvan/vtot.
//
// Invalid input never throws: a bound that is not a real ISO date counts as
// "not chosen", an inverted range is dropped, and a compare period without a
// primary period and without vgl=1 means no period at all. Null = all data.

import isRangeInverted from './isRangeInverted'

const KEYS = { start: 'van', end: 'tot', compareStart: 'vvan', compareEnd: 'vtot', open: 'vgl' }

/**
 * @typedef {object} CardPeriodValue
 * @property {{start: string|null, end: string|null}} period - Primary period (ISO dates)
 * @property {{enabled: boolean, start: string|null, end: string|null}} compare - Compare period
 */

/** @param {string|null} raw @returns {string|null} the ISO date when it names a real calendar day */
const validIsoDate = (raw) => {
    if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
    const [y, m, d] = raw.split('-').map(Number)
    const date = new Date(Date.UTC(y, m - 1, d))
    const real = date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d
    return real ? raw : null
}

/**
 * @param {URLSearchParams} params
 * @param {string} startKey
 * @param {string} endKey
 * @returns {{start: string|null, end: string|null}|null} null when unset or inverted
 */
const readRange = (params, startKey, endKey) => {
    const range = { start: validIsoDate(params.get(startKey)), end: validIsoDate(params.get(endKey)) }
    if (!range.start && !range.end) return null
    return isRangeInverted(range) ? null : range
}

/**
 * @param {string|null|undefined} search - `location.search` (with or without '?')
 * @returns {CardPeriodValue|null} null = no period chosen (all data)
 */
export const parsePeriodQuery = (search) => {
    const params = new URLSearchParams(search || '')
    const openHalf = params.get(KEYS.open) === '1'
    const hasPrimaryKeys = params.has(KEYS.start) || params.has(KEYS.end)
    const period = readRange(params, KEYS.start, KEYS.end)
    // A primary period that is there but unusable (inverted, nonsense) means
    // all data, also when comparing.
    if (!period && (!openHalf || hasPrimaryKeys)) return null
    const compare = readRange(params, KEYS.compareStart, KEYS.compareEnd)
    const hasCompareKeys = params.has(KEYS.compareStart) || params.has(KEYS.compareEnd)
    const compareEnabled = Boolean(compare) || (openHalf && !hasCompareKeys)
    if (!period && !compareEnabled) return null
    return {
        period: period || { start: null, end: null },
        compare: compareEnabled ? { enabled: true, start: compare?.start || null, end: compare?.end || null } : { enabled: false, start: null, end: null },
    }
}

/**
 * @param {Partial<CardPeriodValue>|null|undefined} value - The period to write
 * @param {string} [search] - Current `location.search`; other params are kept
 * @returns {string} '' when nothing is left, else '?…'
 */
export const buildPeriodQuery = (value, search = '') => {
    const params = new URLSearchParams(search || '')
    Object.values(KEYS).forEach((key) => params.delete(key))

    const start = value?.period?.start || null
    const end = value?.period?.end || null
    const comparing = value?.compare?.enabled === true
    const compareStart = comparing ? value.compare.start || null : null
    const compareEnd = comparing ? value.compare.end || null : null
    const primarySet = Boolean(start || end)
    const compareSet = Boolean(compareStart || compareEnd)
    if (primarySet || comparing) {
        if (start) params.set(KEYS.start, start)
        if (end) params.set(KEYS.end, end)
        if (compareStart) params.set(KEYS.compareStart, compareStart)
        if (compareEnd) params.set(KEYS.compareEnd, compareEnd)
        if (comparing && !(primarySet && compareSet)) params.set(KEYS.open, '1')
    }

    const query = params.toString()
    return query ? `?${query}` : ''
}

const periodQuery = { parsePeriodQuery, buildPeriodQuery }

export default periodQuery
